import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import { usePageTracking } from './hooks/monitoring';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { LoadingSpinner } from './components/ui';
import { ProtectedRoute, } from './components/ProtectedRoute';
import { useAuth } from './hooks/useAuth';
import './App.css';
import Home from './pages/Home';
import Settings from './pages/Settings';

// AuthProvider (wrapping the whole app in main.tsx) already blocks
// rendering until its own auth check resolves, so `isAuthenticated` here
// is never a stale/loading guess — no flash of the marketing Home page
// before redirecting a logged-in user straight to their Dashboard.
//
// The redirect only applies to landing on "/" cold (a fresh load, or any
// link that doesn't say otherwise) — Sidebar's own "Home" nav item is a
// deliberate click on this exact page, and marks its navigation with
// `state.intentional` specifically so it isn't immediately bounced back
// out by this same redirect.
const RootRoute = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const intentional = (location.state as { intentional?: boolean } | null)?.intentional;
  return isAuthenticated && !intentional ? <Navigate to="/dashboard" replace /> : <Home />;
};

// Lazy load pages for better performance
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Inventory = lazy(() => import('./pages/Inventory'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const Warehouse = lazy(() => import('./pages/Warehouse'));
const LocationManagement = lazy(() => import('./pages/LocationManagement'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const POS = lazy(() => import('./pages/POS'));
const Parties = lazy(() => import('./pages/Parties'));
const Reports = lazy(() => import('./pages/Reports'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const Contact = lazy(() => import('./pages/Contact'));
const UIComponents = lazy(() => import('./pages/UIComponents'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const DataExchangeConfigPage = lazy(() => import('./pages/DataExchangeConfigPage'));
const CustomFieldsManager = lazy(() => import('./pages/CustomFieldsManager'));
const OrgDetails = lazy(() => import('./pages/OrgDetails'));
const TaxRates = lazy(() => import('./pages/TaxRates'));
const Brands = lazy(() => import('./pages/Brands'));
const HsnCodes = lazy(() => import('./pages/HsnCodes'));
const BillExtractionTemplates = lazy(() => import('./pages/BillExtractionTemplates'));

// Article Management Pages
const Articles = lazy(() => import('./pages/Articles'));

// Supplier Management Pages
const Suppliers = lazy(() => import('./pages/Suppliers'));
const SupplierForm = lazy(() => import('./pages/SupplierForm'));

// Billing
const InvoiceSharePage = lazy(() => import('./pages/InvoiceSharePage'));

// Inventory Management Pages
const Movement = lazy(() => import('./pages/Movement'));
const Audits = lazy(() => import('./pages/Audits'));
const ImportManager = lazy(() => import('./pages/ImportManager'));
const ImportConfig = lazy(() => import('./pages/ImportConfig'));

function App() {
  // Automatically track page views
  usePageTracking();

  return (
    <ThemeProvider>
      <ToastProvider position="top-right">
        <ScrollToTop />
        <Layout>
<Suspense fallback={<LoadingSpinner />}>
  <Routes>
    {/* Public Routes */}
    <Route path="/" element={<RootRoute />} />
    <Route path="/about" element={<About />} />
    <Route path="/services" element={<Services />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/ui-components" element={<UIComponents />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/terms" element={<TermsOfService />} />

    <Route path="/invoice/share/:token" element={<InvoiceSharePage />} />

    {/* Protected Routes */}
    <Route element={<ProtectedRoute />}>
      <Route path="/dashboard" element={<Dashboard />} />

      {/* Settings */}
      <Route path="/settings" element={<Settings />}>
        <Route index element={<DataExchangeConfigPage />} />
        <Route path="data-exchange-manager" element={<DataExchangeConfigPage />} />
        <Route path="custom-fields" element={<CustomFieldsManager />} />
        <Route path="org" element={<OrgDetails />} />
        <Route path="tax-rates" element={<TaxRates />} />
        <Route path="brands" element={<Brands />} />
        <Route path="hsn-codes" element={<HsnCodes />} />
        <Route path="bill-extraction-templates" element={<BillExtractionTemplates />} />
      </Route>

      {/* Warehouse */}
      <Route path="/warehouse" element={<Warehouse />} />
      <Route
        path="/warehouse/:warehouseId/locations"
        element={<LocationManagement />}
      />

      {/* Inventory */}
      <Route path="/inventory">
        <Route index element={<Inventory />} />
        <Route path="movement" element={<Movement />} />
        <Route path="audits" element={<Audits />} />
      </Route>

      {/* Orders & Reports */}
      <Route path="/pos" element={<POS />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/orders/purchase" element={<Orders orderType="PURCHASE" />} />
      <Route path="/orders/sales" element={<Orders orderType="SALES" />} />
      <Route path="/orders/new" element={<OrderDetail />} />
      <Route path="/orders/:id" element={<OrderDetail />} />
      <Route path="/parties" element={<Parties />} />
      <Route path="/reports" element={<Reports />} />
      <Route path="/audit-log" element={<AuditLog />} />
      <Route path="/transactions" element={<TransactionsPage />} />

      {/* Article Management */}
      <Route path="/articles" element={<Articles />} />

      {/* Supplier Management */}
      <Route path="/suppliers">
        <Route index element={<Suppliers />} />
        <Route path="new" element={<SupplierForm />} />
        <Route path=":id" element={<SupplierForm />} />
        <Route path=":id/edit" element={<SupplierForm />} />
      </Route>
    </Route>

    {/* Admin-only Routes */}
    <Route element={<ProtectedRoute />}>
      <Route path="/admin" element={<AdminPanel />} />
    </Route>

    {/* 404 - Not Found */}
    <Route path="*" element={<NotFound />} />
  </Routes>
</Suspense>

        </Layout>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
